import { useCallback } from 'react'
import { PRIMARY_KEY_TO_PATH } from '@/config/navigation/primaryNav'
import { HOME_PRODUCT_KEYS, SECONDARY_VIEW_KEYS } from '@/config/navigation/secondaryNav'
import { DEFAULT_SETTINGS_SECTION_ID } from '@/data/settingsNavData'
import { useSettingsModal } from '@/components/Settings/SettingsModal/SettingsModalContext'
import PATHS, { buildHomeThreadPath } from '@/routes/paths'
import { useAppShellBrowseNavHandlers } from './useAppShellBrowseNavHandlers.js'

export function useAppShellNavHandlers({
  location,
  navigate,
  setSelectedThreadId,
  setHomeView,
  setHomeProduct,
  setMainAskBarPrefill,
}) {
  const { openSettings } = useSettingsModal()
  const browse = useAppShellBrowseNavHandlers({
    navigate,
    setSelectedThreadId,
    setHomeView,
    setHomeProduct,
  })

  const handlePrimaryNavChange = useCallback(
    (key) => {
      if (key === 'settings') {
        openSettings(DEFAULT_SETTINGS_SECTION_ID)
        return
      }
      const nextPath = PRIMARY_KEY_TO_PATH[key]
      if (nextPath && nextPath !== location.pathname) navigate(nextPath)
      setSelectedThreadId(null)
      setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
    },
    [location.pathname, navigate, openSettings, setSelectedThreadId, setHomeView],
  )

  const handleSecondaryThreadSelect = useCallback(
    (thread) => {
      setHomeProduct(HOME_PRODUCT_KEYS.CHAT)
      setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
      setSelectedThreadId(thread.id)
      navigate(buildHomeThreadPath(thread.id))
    },
    [navigate, setHomeProduct, setHomeView, setSelectedThreadId],
  )

  const handlePrimaryDoChat = useCallback(() => {
    setHomeProduct(HOME_PRODUCT_KEYS.CHAT)
    setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
    if (location.pathname === PATHS.DO_VOICE || location.pathname === PATHS.BUILD) {
      setSelectedThreadId(null)
      setMainAskBarPrefill?.({ text: '', nonce: Date.now() })
      navigate(PATHS.HOME)
    }
  }, [location.pathname, navigate, setHomeProduct, setHomeView, setMainAskBarPrefill, setSelectedThreadId])

  const handlePrimaryDoVoice = useCallback(() => {
    setHomeProduct(HOME_PRODUCT_KEYS.VOICE)
    setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
    setSelectedThreadId(null)
    navigate(PATHS.DO_VOICE)
  }, [navigate, setHomeProduct, setHomeView, setSelectedThreadId])

  const handlePrimaryDoCode = useCallback(() => {
    setHomeProduct(HOME_PRODUCT_KEYS.CODE)
    setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
    setSelectedThreadId(null)
    navigate(PATHS.BUILD)
  }, [navigate, setHomeProduct, setHomeView, setSelectedThreadId])

  const handleSecondaryNewChat = useCallback(() => {
    setHomeProduct(HOME_PRODUCT_KEYS.CHAT)
    setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
    setSelectedThreadId(null)
    setMainAskBarPrefill?.({ text: '', nonce: Date.now() })
    navigate(PATHS.HOME)
  }, [navigate, setHomeProduct, setHomeView, setSelectedThreadId, setMainAskBarPrefill])

  const handleSecondaryDocuments = useCallback(() => {
    setHomeProduct(HOME_PRODUCT_KEYS.CHAT)
    setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
    setSelectedThreadId(null)
    navigate(PATHS.DOCUMENTS)
  }, [navigate, setHomeProduct, setHomeView, setSelectedThreadId])

  const handleSecondaryDoVoice = useCallback(() => {
    setHomeProduct(HOME_PRODUCT_KEYS.VOICE)
    setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
    setSelectedThreadId(null)
    navigate(PATHS.DO_VOICE)
  }, [navigate, setHomeProduct, setHomeView, setSelectedThreadId])

  const handleSecondaryBuild = useCallback(() => {
    setHomeProduct(HOME_PRODUCT_KEYS.CODE)
    setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
    setSelectedThreadId(null)
    navigate(PATHS.BUILD)
  }, [navigate, setHomeProduct, setHomeView, setSelectedThreadId])

  const handleSecondaryMyApps = useCallback(() => {
    setHomeProduct(HOME_PRODUCT_KEYS.CODE)
    setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
    setSelectedThreadId(null)
    navigate(PATHS.HOME)
  }, [navigate, setHomeProduct, setHomeView, setSelectedThreadId])

  const handleSecondaryNavAction = useCallback(
    (section) => {
      if (section?.threadId) {
        handleSecondaryThreadSelect({ id: section.threadId, title: section.label ?? '' })
        return
      }
      setHomeView(SECONDARY_VIEW_KEYS.HOME_MAIN)
      navigate(PATHS.HOME)
    },
    [handleSecondaryThreadSelect, navigate, setHomeView],
  )

  return {
    handlePrimaryNavChange,
    handlePrimaryDoChat,
    handlePrimaryDoVoice,
    handlePrimaryDoCode,
    handleSecondaryThreadSelect,
    handleSecondaryNewChat,
    handleSecondaryDocuments,
    handleSecondaryDoVoice,
    handleSecondaryBuild,
    handleSecondaryMyApps,
    ...browse,
    handleSecondaryNavAction,
  }
}
